import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Lock, ShieldCheck, Loader2, AlertTriangle, PlayCircle } from "lucide-react";
import logo from "@/assets/boomerang_logo.png";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const REASONS = {
  invalid: "Ce lien de démonstration est invalide.",
  used: "Ce lien a déjà été utilisé. Demandez-nous un nouveau lien.",
  expired: "Ce lien de démonstration a expiré (valable 72 h).",
};

export default function DemoAccess() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null); // { valid, email, reason }
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/demo/invite/${token}`);
        setInvite(r.data);
      } catch {
        setInvite({ valid: false, reason: "invalid" });
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.post("/demo/redeem", { token, password });
      setUser(res.data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="w-full lg:w-[46%] flex flex-col justify-center px-8 sm:px-16">
        <div className="max-w-md w-full mx-auto">
          <img src={logo} alt="Boomerang Pacific Tracking" className="h-14 w-auto mb-10" data-testid="demo-logo" />
          <p className="label-mono mb-3">Accès démonstration</p>

          {checking ? (
            <div className="flex items-center gap-2 text-muted-foreground" data-testid="demo-checking">
              <Loader2 className="w-5 h-5 animate-spin" /> Vérification du lien…
            </div>
          ) : !invite?.valid ? (
            <div data-testid="demo-invalid">
              <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
                <AlertTriangle className="w-7 h-7 text-[#F5821F]" /> Lien indisponible
              </h1>
              <p className="mt-3 text-muted-foreground text-sm">{REASONS[invite?.reason] || REASONS.invalid}</p>
              <button onClick={() => navigate("/")} data-testid="demo-gohome"
                className="mt-6 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold rounded-md px-6 py-3 text-sm transition-colors">
                Aller à la connexion
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Démo Boomerang</h1>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                Bienvenue ! Saisissez le mot de passe fourni dans votre email d'invitation pour accéder à la démonstration (lecture seule).
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="demo-form">
                <div className="relative">
                  <ShieldCheck className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="email" value={invite.email || ""} readOnly data-testid="demo-email"
                    className="w-full bg-secondary/40 border border-border rounded-md pl-11 pr-4 py-3 text-sm outline-none text-muted-foreground" />
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)}
                    required data-testid="demo-password"
                    className="w-full bg-secondary/60 border border-border rounded-md pl-11 pr-4 py-3 text-sm outline-none focus:border-[#17A2B8] text-foreground placeholder:text-muted-foreground" />
                </div>
                {error && <p className="text-sm text-red-400" data-testid="demo-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="demo-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-6 py-3.5 transition-colors">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><PlayCircle className="w-5 h-5" /> Accéder à la démo</>)}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1760127831098-6a75c69ee79c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(9,9,11,0.95) 0%, rgba(9,9,11,0.4) 55%, rgba(23,162,184,0.25) 100%)" }} />
        <div className="absolute bottom-10 left-10 right-10 rounded-lg p-6 border border-white/10" style={{ background: "rgba(9,9,11,0.55)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <p className="label-mono mb-1">Boomerang Pacific Tracking</p>
          <p className="text-lg text-white font-semibold">Découvrez le suivi de flotte en temps réel</p>
        </div>
      </div>
    </div>
  );
}
